import React, { useEffect, useRef } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { Typography, Box, FormControl, Select, MenuItem } from "@mui/material";

const Match = ({
  matchSelections,
  currentActivity,
  submitted,
  handleSelectChange,
  shuffledValues,
  shuffledNames,
}) => {
  const rationaleRef = useRef();
  const uniqueShuffledValues = shuffledValues.filter(
    (value, index, self) => self.indexOf(value) === index
  );
  useEffect(() => {
    if (submitted && rationaleRef.current) {
      setTimeout(() => {
        rationaleRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100); // Adjust delay if needed
    }
  }, [submitted]);
  const isArabic = (text) => /[\u0600-\u06FF]/.test(text);

  let combinedRationale = [];
  let isQuestionCorrect = false;

  if (submitted) {
    isQuestionCorrect = currentActivity.Options.every((option) => {
      // Find the selected value in matchSelections based on the value
      const selectedValue = Object.values(matchSelections).find(
        (value) => value === option.Value
      );
      return selectedValue === option.Value; // Match selected answer with the correct one
    });
    // Combine all rationales for the current question
    combinedRationale = currentActivity.Options.map((option) => {
      return option.Rationale?.Text || null;
    })
      .filter((text) => text) // Remove null or empty texts
      .join(" "); // Combine them into a single string
  }
  // Check if the entire question is correct
  return (
    <>
      <Box sx={{ marginTop: "10px" }}>
        {/* Column headers */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "8px 16px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            fontWeight: "bold",
            fontFamily: "Traditional Arabic, serif",
          }}
        >
          <Typography sx={{ flex: 1 }}>#</Typography>
          <Typography sx={{ flex: 3 }}>Name</Typography>
          <Typography sx={{ flex: 3 }}>Match</Typography>
        </Box>

        {shuffledNames.map((name, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              minHeight: "64px", // taller rows
              padding: "10px 16px",
              backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
              borderBottom: "1px solid #eee",
            }}
          >
            {/* Row number */}
            <Typography
              sx={{ flex: 1, fontWeight: "bold", fontSize: "1.1rem" }}
            >
              {index + 1}
            </Typography>

            {/* Name or image */}
            {/^https?:\/\//.test(name) ? (
              <Box sx={{ flex: 3 }}>
                <img
                  src={name}
                  alt={`Item ${index + 1}`}
                  style={{
                    maxWidth: "100px",
                    maxHeight: "50px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Typography
                sx={{
                  flex: 3,
                  textAlign: "left",
                  wordBreak: "break-word",
                  fontFamily: "Traditional Arabic, serif",
                  fontSize: isArabic(name) ? "1.5rem" : "1.1rem",
                }}
              >
                {name}
              </Typography>
            )}

            {/* Dropdown */}
            <FormControl variant="outlined" sx={{ flex: 3 }}>
              <Select
                value={
                  currentActivity.ContentId === "preview"
                    ? undefined // make Select uncontrolled in preview
                    : matchSelections[index] || ""
                }
                defaultValue={
                  currentActivity.ContentId === "preview" ? "" : undefined
                }
                onChange={(e) => {
                  handleSelectChange?.(index, e.target.value);
                }}
                disabled={submitted}
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: { style: { maxHeight: 200 } },
                }}
                sx={{ fontSize: "1.1rem" }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {uniqueShuffledValues.map((value, valueIndex) => (
                  <MenuItem
                    key={valueIndex}
                    value={value}
                    sx={{ fontSize: "1.5rem" }}
                  >
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ))}
        {submitted && !isQuestionCorrect && (
          <Box
            ref={rationaleRef}
            sx={{
              marginTop: 4,
              padding: 2,
              borderRadius: 2,
              backgroundColor: "#ffebee",
              textAlign: "center",
            }}
          >
            <CancelIcon sx={{ fontSize: 40, color: "red" }} />
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "1rem",
                color: "red",
                marginTop: 1,
              }}
            >
              Incorrect!
            </Typography>

            {combinedRationale ? (
              <Typography
                sx={{
                  marginTop: 1,
                  fontSize: "0.85rem",
                  color: "text.secondary",
                  padding: "8px",
                  margin: "8px 0",
                  whiteSpace: "pre-wrap",
                }}
              >
                {combinedRationale}
              </Typography>
            ) : (
              <Typography
                sx={{
                  marginTop: 1,
                  fontSize: "0.85rem",
                  fontStyle: "italic",
                  color: "text.secondary",
                  padding: "8px",
                  margin: "8px 0",
                }}
              >
                Rationale is not available.
              </Typography>
            )}
          </Box>
        )}

        {submitted && isQuestionCorrect && (
          <Box
            sx={{
              marginTop: 4,
              padding: 2,
              borderRadius: 2,
              backgroundColor: "#e8f5e9",
              textAlign: "center",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "1rem",
                color: "green",
                marginTop: 1,
              }}
            >
              Correct!
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default Match;
